import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from 'projects/cms/src/app/core/http/http.service';
import { map, Observable, of, Subscriber, switchMap } from 'rxjs';

import { FileUploadProgress, FileUploadService } from '../components/files/file-upload.service';
import { NewFile, NewFileBase } from '../components/files/models/File';
import { FileContainerBase } from '../components/files/models/FileContainer';
import { LoadingState } from '../components/files/models/LoadingState.enum';
import { ProgressEvent } from '../components/files/progress/progress.component';
import { Logger } from '../core/logger.service';
import { __ } from '../functions/object.functions';
import { DatabaseFile } from '../models/classes/files/File';
import { ElevenLabsVoice } from '../models/local/ElevenLabsVoice';
import { GoogleVoice } from '../models/local/GoogleVoice';
import { InteractableEntityService } from './interactable-entity.service';
import { Response } from './local/Response';
import { I18nService } from './local/i18n.service';
import { HTTP_SERVICE } from '../injection-tokens/http-service';

const log = new Logger('Courses Service');

@Injectable()
export class FilesService extends InteractableEntityService<DatabaseFile, DatabaseFile, DatabaseFile> {

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor(
        @Inject(HTTP_SERVICE) protected httpService: HttpClient,
        protected i18nService: I18nService
    ) {
        super('files', 'File', httpService, i18nService);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC INSTANCE METHODS
    // -----------------------------------------------------------------------------------------------------

    getElevenLabsVoices(languageId?: string, gender?: string): Observable<ElevenLabsVoice[]> {
        const queryParameters = [];

        if (!__.IsNullOrUndefined(languageId)) {
            queryParameters.push(`languageId=${languageId}`);
        }

        if (!__.IsNullOrUndefined(gender)) {
            queryParameters.push(`gender=${gender}`);
        }

        return this.httpService.get<Response<ElevenLabsVoice[]>>(`files/audios/eleven-labs/voices?${queryParameters.join('&')}`).pipe(map(q => q.data));
    }

    /**
     * Retrieves a pre-signed URL for accessing a specific file variation.
     *
     * @param {string} id - The identifier of the file.
     * @param {string} variationName - The name of the variation of the file.
     * @return {Observable<string>} An observable that emits the pre-signed URL as a string.
     */
    getPreSignedUrl(id: string, variationName: string) : Observable<string> {
        return this.httpService
        .get<Response<string>>(`files/${id}/${variationName}/signed-url`)
        .pipe(
            map((response: Response<string>) => response.data)
        );
    }

    getGoogleVoices(languageCode?: string, gender?: string, speedRate?: number): Observable<GoogleVoice[]> {
        const queryParameters = [];

        if (!__.IsNullOrUndefined(languageCode)) {
            queryParameters.push(`languageCode=${languageCode}`);
        }

        if (!__.IsNullOrUndefined(gender)) {
            queryParameters.push(`gender=${gender}`);
        }

        if (!__.IsNullOrUndefined(gender)) {
            queryParameters.push(`speedRate=${speedRate}`);
        }

        return this.httpService.get<Response<GoogleVoice[]>>(`files/audios/google/voices?${queryParameters.join('&')}`).pipe(map(q => q.data));
    }

    createElevenLabsAudioFileWithTextToSpeech(text: string, voiceId: string): Observable<{ blob: Blob, name: string }> {
        return this.httpService
            .post<Blob>(
                `files/audios/eleven-labs/text-to-speech`,
                { text, voiceId },
                {
                    observe: 'response', responseType: 'blob' as 'json',
                    headers: {
                        'Access-Control-Expose-Headers': 'x-amz-meta-title'
                    }
                }
            )
            .pipe(
                map((finalResponse: HttpResponse<Blob>) => {
                    return { blob: new Blob([finalResponse.body]), name: this.getFileName(finalResponse) };
                })
            );
    }

    createGoogleAudioFileWithTextToSpeech(text: string, voiceName: string, gender: string, languageCode: string, modelName: string, speakingRate: number, prompt: string): Observable<{ blob: Blob, name: string }> {
        return this.httpService
            .post<Blob>(
                `files/audios/google/text-to-speech`,
                { text, voiceName, gender, languageCode, modelName, speakingRate, prompt },
                {
                    observe: 'response', responseType: 'blob' as 'json',
                    headers: {
                        'Access-Control-Expose-Headers': 'x-amz-meta-title'
                    }
                }
            )
            .pipe(
                map((finalResponse: HttpResponse<Blob>) => {
                    return { blob: new Blob([finalResponse.body]), name: this.getFileName(finalResponse) };
                })
            );
    }

    /**
     * Uploads the provided file and deletes the previously uploaded file beforehand, if one exists
     *
     * @param file The file that should be uploaded
     * @param id The id of the file if it has been uploaded previously
     * @returns An observable containing the file upload progress
     */
    // TODO: Can the id parameter be replaced by file.id ?
    uploadFile$(file: FileContainerBase, data: object, id?: string): Observable<DatabaseFile> {
        let observable: Observable<any> = of(null);

        if (file instanceof FileContainerBase
            && file.original instanceof NewFile && !__.IsNullOrUndefinedOrEmpty(id)) {

            // If an image was previously uploaded, and now a new image was provided, delete the current one
            observable = observable.pipe(
                switchMap(() => {
                    return this.delete$(id);
                })
            );
        }

        if (file instanceof FileContainerBase && file.original instanceof NewFileBase) {
            const fileContainer = file;
            const image = (fileContainer.original as NewFile);

            const formData = new FormData();
            formData.append('file', image.file);

            if (!__.IsNullOrUndefined(data)) {
                for (const key in data) {
                    if (Object.prototype.hasOwnProperty.call(data, key)) {
                        // TODO: What to do, if data[key] is an object?
                        formData.append(key, (data as { [key: string]: any })[key]);
                    }
                }
            }

            observable = observable.pipe(
                switchMap(() => {
                    return new Observable((subscriber: Subscriber<FileUploadProgress | DatabaseFile>) => {
                        super.addSubscription(
                            this.httpService
                                .skipErrorHandler()
                                .post(`files`, formData, { reportProgress: true, observe: 'events' })
                                .pipe(
                                    map((event: any) => {
                                        if (event instanceof HttpResponse) {
                                            return event;
                                        }
                                        return FileUploadService.mapHttpEventToProgress(event);
                                    }),
                                )
                                .subscribe({
                                    next: (innerResponse: FileUploadProgress | HttpResponse<Response<DatabaseFile>> | HttpErrorResponse) => {
                                        if (innerResponse instanceof FileUploadProgress) {
                                            fileContainer.loadingProgress = { progress: innerResponse.progress, loadingState: innerResponse.state } as ProgressEvent;

                                            if (innerResponse.state === LoadingState.Completed) {
                                                subscriber.next(innerResponse);
                                                subscriber.complete();
                                            }
                                        } else if (innerResponse instanceof HttpResponse) {
                                            fileContainer.loadingProgress = { progress: 100, loadingState: LoadingState.Completed } as ProgressEvent;
                                            subscriber.next((innerResponse.body as Response<DatabaseFile>).data);
                                        } else if (innerResponse instanceof HttpErrorResponse) {
                                            fileContainer.loadingProgress = { progress: 100, loadingState: LoadingState.Erroneous } as ProgressEvent;
                                            subscriber.next((innerResponse.error as Response<any>).data);
                                        }
                                    },
                                    error: (error: HttpErrorResponse) => {
                                        subscriber.error(error);
                                    }
                                })
                        );
                    })
                })
            );
        }

        return observable;
    }

    delete$(id: string): Observable<any> {
        return this.httpService
            .skipErrorHandler()
            .delete(`files/${id}`)
    }

    downloadFile(
        url: string,
        convertTo: 'any' | 'Uint8Array' | 'Blob' = 'Blob',
        isS3Call: boolean = false
    ): Observable<{ blob: Blob, name: string }> {

        let httpClient = this.httpService;

        if (isS3Call) {
            httpClient = httpClient.disableApiPrefix();
        }

        if (isS3Call) {
            httpClient = httpClient.disableAccessToken();
        }

        return httpClient
            .get<Blob>(
                `${url}`,
                {
                    observe: 'response', responseType: 'blob' as 'json',
                    headers: {
                        'Access-Control-Expose-Headers': 'x-amz-meta-title'
                    }
                }
            )
            .pipe(
                map((finalResponse: HttpResponse<Blob>) => {
                    switch (convertTo) {
                        case 'Blob':
                            return { blob: new Blob([finalResponse.body]), name: this.getFileName(finalResponse) };
                        default:
                            return null;
                    }
                })
            )
    }

    downloadFileByIds(ids: string[], mode: 'combine'): Observable<{ blob: Blob, name: string }> {
        const idsParameter = ids.map(q => `ids=${q}`).join('&');

        return this.httpService
            .get<Blob>(
                `files?mode=${mode}&${idsParameter}`,
                {
                    observe: 'response', responseType: 'blob' as 'json',
                }
            )
            .pipe(
                map((finalResponse: HttpResponse<Blob>) => {
                    return { blob: new Blob([finalResponse.body]), name: this.getFileName(finalResponse) };
                })
            );
    }

    private getFileName(response: HttpResponse<Blob>) {
        let filename: string;

        try {
            const contentDisposition: string = response.headers.get('content-disposition');
            filename = contentDisposition.substring(
                contentDisposition.lastIndexOf('filename=') + 9,
                contentDisposition.lastIndexOf(';')
            );
            filename = filename.replace('"', '');
            filename = filename.replace('"', '');
        }
        catch (e) {
            try {
                filename = response.headers.get('x-amz-meta-title');
            }
            catch (e) {
                filename = 'myfile.pdf';
            }
        }

        return filename;
    }

}
